<template>
  <div>
    <v-card>
      <v-card-title>
      <v-icon color="primary">
        mdi-bus-stop
      </v-icon>
        <span class="px-2">
            {{ $t("Stops") }}
        </span>
        <v-spacer></v-spacer>
        <create-button @create="createStop"></create-button>
        <activation-tool-tip model="stops"></activation-tool-tip>
      </v-card-title>
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="stops"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>

        <template v-slot:item.routes="{ item }">
          <a v-if="item.routes.length" @click.stop="displayRoutes(stops.indexOf(item))">{{ item.routes.length }}</a>
          <span v-else>{{$t('no_routes')}}</span>
        </template>

        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="mx-1" v-bind="attrs" v-on="on" @click="viewStop(item)">
                mdi-eye
              </v-icon>
            </template>
            <span>{{ $t('view') || 'عرض' }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="mx-1 text-primary" v-bind="attrs" v-on="on" @click="openRenameDialog(item)">
                mdi-pencil
              </v-icon>
            </template>
            <span>تعديل اسم المحطة</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="mx-1 text-danger" v-bind="attrs" v-on="on" @click="deleteStop(item, stops.indexOf(item))">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') || 'حذف' }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-if="stops[selectedStop]" v-model="dialog" max-width="290">
      <v-card>
        <v-card-title class="text-h5">
          {{ stops[selectedStop].name }}
        </v-card-title>

        <v-card-text>
          <v-list dense>
            <v-subheader>{{$t('routes')}}</v-subheader>
            <v-list-item-group>
              <v-list-item
                v-for="(item, i) in stops[selectedStop].routes"
                :key="i"
              >
                <v-icon small class="mx-2">
                  mdi-eye
                </v-icon>
                <v-list-item-content>
                  <v-list-item-title v-text="item.name" @click="viewRoute(item)"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="green darken-1" text @click="dialog = false">
            {{$t('close')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Direct Stop Rename Dialog -->
    <v-dialog v-model="renameDialog.visible" max-width="480" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pt-4 px-5">
          <div class="d-flex align-center">
            <v-icon color="#082C69" class="me-2">mdi-pencil-box-outline</v-icon>
            <span class="font-weight-bold text-subtitle-1" style="color: #082C69;">تعديل اسم المحطة</span>
          </div>
          <v-btn icon small @click="closeRenameDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-5 px-5">
          <p class="caption text-muted mb-3" v-if="renameDialog.stop">
            تعديل الاسم يحافظ على موقع المحطة الجغرافي ({{ renameDialog.stop.lat }}, {{ renameDialog.stop.lng }}) وكافة ارتباطاتها بالمسارات السابقة والحالية دون إنشاء مكرر.
          </p>
          <v-text-field
            v-model="renameDialog.name"
            label="اسم المحطة الجديد"
            outlined
            dense
            autofocus
            :error-messages="renameDialog.error"
            placeholder="أدخل اسم المحطة الجديد"
            @keyup.enter="saveRenameStop"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-5 py-3">
          <v-spacer></v-spacer>
          <v-btn text color="#64748B" :disabled="renameDialog.loading" @click="closeRenameDialog">
            إلغاء
          </v-btn>
          <v-btn
            depressed
            color="#082C69"
            dark
            :loading="renameDialog.loading"
            :disabled="!renameDialog.name || !renameDialog.name.trim() || renameDialog.loading"
            @click="saveRenameStop"
          >
            حفظ الاسم
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ActivationToolTip from "@/components/ActivationToolTip";
import CreateButton from "@/components/CreateButton";

export default {
  components: {
    ActivationToolTip,
    CreateButton,
  },
  data() {
    return {
      stops: [],
      dialog: false,
      isLoading: false,
      selectedStop: null,
      search: "",
      renameDialog: {
        visible: false,
        loading: false,
        stop: null,
        name: "",
        error: "",
      },
      headers: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Name", value: "name" },
        { text: "address", value: "address", width: 300 },
        { text: "Routes", value: "routes" },
        { text: "created_at", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page"),
      }
    }
  },
  mounted() {
    this.loadStops();
  },
  methods: {
    displayRoutes(index) {
      this.selectedStop = index;
      this.dialog = true;
    },
    viewRoute(route) {
      this.$router.push({
        name: "view-route",
        params: { route_id: route.id},
      });
    },
    loadStops() {
      this.isLoading = true;
      this.stops = [];
      axios
        .get(`/stops/all`)
        .then((response) => {
          this.stops = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("error_retrieving_stops"),
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    createStop() {
      this.$router.push({
        name: "create-stop",
      });
    },
    viewStop(stop)
    {
      this.$router.push({
        name: "view-stop",
        params: {
          stop_id: stop.id,
        },
      });
    },
    editStop(stop)
    {
      this.openRenameDialog(stop);
    },
    openRenameDialog(stop) {
      this.renameDialog = {
        visible: true,
        loading: false,
        stop: stop,
        name: stop.name || "",
        error: "",
      };
    },
    closeRenameDialog() {
      this.renameDialog.visible = false;
      this.renameDialog.stop = null;
      this.renameDialog.name = "";
      this.renameDialog.error = "";
      this.renameDialog.loading = false;
    },
    saveRenameStop() {
      if (!this.renameDialog.stop || !this.renameDialog.name) return;
      const trimmed = this.renameDialog.name.trim();
      if (!trimmed) {
        this.renameDialog.error = "اسم المحطة مطلوب ولا يمكن أن يكون فارغاً.";
        return;
      }
      this.renameDialog.loading = true;
      this.renameDialog.error = "";
      axios
        .put(`/stops/${this.renameDialog.stop.id}/rename`, { name: trimmed })
        .then((response) => {
          this.renameDialog.stop.name = trimmed;
          this.$notify({
            title: this.$t("Success") || "نجاح",
            text: response.data.message || "تم تعديل اسم المحطة بنجاح.",
            type: "success",
          });
          this.closeRenameDialog();
        })
        .catch((error) => {
          const msg =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "حدث خطأ أثناء تعديل اسم المحطة.";
          this.renameDialog.error = msg;
          this.$notify({
            title: this.$t("Error") || "خطأ",
            text: msg,
            type: "error",
          });
        })
        .finally(() => {
          this.renameDialog.loading = false;
        });
    },
    deleteStop(stop, index) {
      if(stop.routes.length>0)
      {
        this.$swal
        .fire({
          title: this.$t("can_not_delete"),
          text: this.$t("cannot_delete_stop_in_route", { name: stop.name }),
          icon: "error",
        });
      }
      else
      {
        this.$swal
          .fire({
            title: this.$t("delete_stop"),
            text: this.$t("confirm_delete_stop", { name: stop.name }),
            icon: "error",
            showCancelButton: true,
            confirmButtonText: this.$t("yes_delete"),
            cancelButtonText: this.$t("cancel"),
          })
          .then((result) => {
            if (result.isConfirmed) {
              this.deleteStopServer(stop.id, index);
            }
          });
      }
    },
    deleteStopServer(stop_id, index) {
      axios
        .delete(`/stops/${stop_id}`)
        .then((response) => {
          this.stops.splice(index, 1);
          this.$notify({
            title: this.$t("Success"),
            text: this.$t("stop_deleted"),
            type: "success",
          });
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: "Error while deleting stops",
            type: "error",
          });
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          //this.isDeleting = false;
        });
    },
  },
};
</script>

<style lang="scss">
.theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover{
  cursor: pointer;
  background: rgba($primary-shade--light, 0.15) !important;
}
</style>
